import { useState, useEffect, useCallback } from 'react';
import { Download, Trash2, FileText, Paperclip } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { purchasingAPI, ASNDocument, ASNDocumentType } from '../../lib/api';
import { DOCUMENT_TYPE_LABELS } from './asnModalTypes';
import { useConfirm } from '../../contexts/ConfirmDialogContext';
import { useASNDocumentUpload, ASN_ACCEPT_ATTR, ASN_MAX_FILE_SIZE } from './useASNDocumentUpload';
import { ASNUploadFailures } from './ASNUploadFailures';
import FileDropZone from '../common/FileDropZone';


interface ASNLineDocumentsProps {
  isOpen: boolean;
  onClose: () => void;
  asnId: number;
  deliveryLineId: number;
  lineLabel: string;
  /** Called after upload/delete so the parent can refresh its per-line counts. */
  onChanged?: () => void;
}

/**
 * CR-5762: per-delivery-line document attachments for an ASN. Lists, uploads,
 * downloads, and deletes documents scoped to a single delivery line (the
 * backend ASNDocument carries an optional delivery_line FK). ASN-level
 * documents continue to live in the ASN Documents tab.
 */
export function ASNLineDocuments({ isOpen, onClose, asnId, deliveryLineId, lineLabel, onChanged }: ASNLineDocumentsProps) {
  const confirm = useConfirm();
  const [documents, setDocuments] = useState<ASNDocument[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedDocType, setSelectedDocType] = useState<ASNDocumentType>('delivery_note');

  useEffect(() => {
    if (isOpen && deliveryLineId) fetchDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, deliveryLineId]);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const res = await purchasingAPI.getASNDocuments({ asn: asnId, delivery_line: deliveryLineId, page_size: 100 });
      setDocuments(res.data.results || []);
    } catch (error) {
      console.error('Failed to fetch line documents:', error);
    } finally {
      setLoading(false);
    }
  };

  // CR-7163 (b): multi-file + drag-and-drop, shared with ASNDocumentsTab.
  const upload = useASNDocumentUpload({
    buildFormData: useCallback(() => {
      const formData = new FormData();
      formData.append('asn', asnId.toString());
      formData.append('delivery_line', deliveryLineId.toString());
      formData.append('document_type', selectedDocType);
      return formData;
    }, [asnId, deliveryLineId, selectedDocType]),
    onUploaded: useCallback(async () => {
      await fetchDocuments();
      onChanged?.();
    }, [onChanged]),  // eslint-disable-line react-hooks/exhaustive-deps
  });

  const handleDownload = async (doc: ASNDocument) => {
    try {
      const response = await purchasingAPI.downloadASNDocument(doc.id);
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.file_name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download document:', error);
    }
  };

  const handleDelete = async (docId: number) => {
    const ok = await confirm({ title: 'Delete Document', description: 'Are you sure you want to delete this document?', confirmLabel: 'Delete', variant: 'destructive' });
    if (!ok) return;
    try {
      await purchasingAPI.deleteASNDocument(docId);
      await fetchDocuments();
      onChanged?.();
    } catch (error) {
      console.error('Failed to delete document:', error);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Paperclip className="h-4 w-4" /> Line Attachments — {lineLabel}
          </DialogTitle>
        </DialogHeader>

        {/* Customer review 11 Sep (drag and drop everywhere): shared FileDropZone. */}
        <div className="space-y-4">
          <div className="rounded-lg p-4 bg-muted/30 space-y-3">
            <div className="max-w-xs">
              <label className="block text-xs font-medium text-muted-foreground mb-1">Document Type</label>
              <select value={selectedDocType} onChange={(e) => setSelectedDocType(e.target.value as ASNDocumentType)} className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary">
                {Object.entries(DOCUMENT_TYPE_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
            <FileDropZone
              multiple
              size="compact"
              accept={ASN_ACCEPT_ATTR}
              maxSize={ASN_MAX_FILE_SIZE}
              busy={upload.uploading}
              label={upload.uploading
                ? `Uploading ${Math.min(upload.progress.done + 1, upload.progress.total)} of ${upload.progress.total}...`
                : undefined}
              hint="Max 50MB each"
              onFiles={upload.uploadFiles}
              data-testid="asn-line-documents-drop-zone"
            />
            <ASNUploadFailures failed={upload.failed} onDismiss={upload.dismissFailures} />
          </div>

          {loading ? (
            <div className="text-sm text-muted-foreground py-6 text-center">Loading documents...</div>
          ) : documents.length === 0 ? (
            <div className="text-sm text-muted-foreground py-8 text-center border rounded-lg bg-muted/10">No documents attached to this line yet</div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/30">
                    <th className="text-left py-2 px-3 font-medium">Document</th>
                    <th className="text-left py-2 px-3 font-medium">Type</th>
                    <th className="text-left py-2 px-3 font-medium">Size</th>
                    <th className="text-right py-2 px-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {documents.map((doc) => (
                    <tr key={doc.id} className="border-b last:border-b-0 hover:bg-muted/30">
                      <td className="py-2 px-3"><div className="flex items-center gap-2"><FileText className="h-4 w-4 text-muted-foreground" /><span className="font-medium truncate max-w-[220px]" title={doc.file_name}>{doc.file_name}</span></div></td>
                      <td className="py-2 px-3"><span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">{DOCUMENT_TYPE_LABELS[doc.document_type] || doc.document_type}</span></td>
                      <td className="py-2 px-3 text-muted-foreground">{doc.formatted_file_size || '-'}</td>
                      <td className="py-2 px-3">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => handleDownload(doc)} className="p-1.5 hover:bg-blue-100 rounded-lg transition-colors text-blue-600" title="Download"><Download className="h-4 w-4" /></button>
                          <button onClick={() => handleDelete(doc.id)} className="p-1.5 hover:bg-red-100 rounded-lg transition-colors text-red-600" title="Delete"><Trash2 className="h-4 w-4" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
